import React, { createContext, useContext, type ComponentType, type ReactNode } from 'react';

export interface RedeemUxUser {
  _id?: string;
  username?: string;
  roles?: string[];
  balances?: Record<string, number>;
}

export interface RedeemUxPlatformApi {
  revealToken: (tokenKey: string, redeemAmount?: number) => Promise<unknown>;
  redeemToken: (
    tokenKey: string,
    redeemAmount?: number,
    transactor?: 'balance' | 'identity'
  ) => Promise<unknown>;
  payInvoiceToken: (tokenKey: string, redeemInfo: unknown) => Promise<unknown>;
  payInvoice: (paymentData: {
    invoiceCode: string;
    paymentAmount: number;
    currency?: string;
  }) => Promise<unknown>;
  getInvoiceDetails: (invoiceCode: string) => Promise<unknown>;
  getMerchantProfile: () => Promise<unknown>;
  getPreferences: <T = unknown>(preferenceId: string) => Promise<T>;
}

export type RedeemUxToast = (opts: {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive' | string;
}) => void;

export interface RedeemUxI18n {
  __: (key: string, params?: Record<string, unknown>) => string;
}

export type MmMoneyIssuerProfile = {
  issuerUserId: string;
  currencyIconUrl?: string;
  displayName?: string;
  name?: string;
  username?: string;
};

export interface RedeemUxValue {
  platformApi: RedeemUxPlatformApi;
  getUser: () => RedeemUxUser | null | undefined;
  toast: RedeemUxToast;
  i18n: RedeemUxI18n;
  emitTokenRedemptionBalanceUpdate: (
    amount: number,
    currency: string,
    currentBalance: number,
    tokenKey?: string
  ) => void;
  emitBalanceUpdate: (payload: Record<string, unknown>) => void;
  formatCurrency: (amount: number, currency: string) => Promise<string>;
  getCurrencyDetails: (currency: string) => Promise<{ symbol: string; decimals: number }>;
  /** Resolve MM$ issuer branding for redeem UI (merchant name, icon, etc.). */
  fetchMmMoneyIssuerProfile?: (issuerUserId: string) => Promise<MmMoneyIssuerProfile | null>;
  Link: ComponentType<{ to: string; className?: string; children?: ReactNode }>;
  profileAutoDepositTo: string;
  InvoicePaymentReceived: ComponentType<Record<string, unknown>>;
  TopUpBalance: ComponentType<Record<string, unknown>>;
  StripeProvider: ComponentType<{ children: ReactNode }>;
}

const RedeemUxContext = createContext<RedeemUxValue | null>(null);

export function RedeemUxProvider({
  value,
  children,
}: {
  value: RedeemUxValue;
  children: ReactNode;
}) {
  return <RedeemUxContext.Provider value={value}>{children}</RedeemUxContext.Provider>;
}

export function useRedeemUx(): RedeemUxValue {
  const ctx = useContext(RedeemUxContext);
  if (!ctx) {
    throw new Error('useRedeemUx must be used within RedeemUxProvider');
  }
  return ctx;
}
